'use client';
import Link from 'next/link';
import React, { FC, PropsWithChildren, useState } from 'react';
import { IconProps } from '@/utils/types';

type ButtonProps = {
  className?: string;
  loading?: boolean;
  arrow?: boolean;
  arrowLeft?: boolean;
  btn?:
    | 'primary'
    | 'dark'
    | 'transparent'
    | 'ol-white'
    | 'ol-primary'
    | 'ol-dark'
    | '';
  roundedFull?: boolean;
  size?: 'md' | 'sm' | 'xs' | 'lg' | '';
  href?: string;
  target?: '_self' | '_blank';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

const getColorClass = (
  btn: string,
): { colorClass: string; hoverColorClass: string } => {
  const buttonClasses: Record<
    string,
    { colorClass: string; hoverColorClass: string }
  > = {
    primary: {
      colorClass: 'text-white bg-primary-500 ring-0 border-0',
      hoverColorClass: 'bg-red-700',
    },
    dark: {
      colorClass: 'text-white bg-dark dark:bg-white dark:text-dark',
      hoverColorClass: 'bg-white/15 dark:bg-cream-dark',
    },
    transparent: {
      colorClass:
        'text-dark hover:text-primary-500 dark:text-ivory dark:hover:text-primary-500',
      hoverColorClass: '',
    },
    'ol-primary': {
      colorClass: 'text-primary-500 bg-transparent border border-primary-500',
      hoverColorClass: '',
    },
    'ol-dark': {
      colorClass:
        'text-dark-500 bg-transparent border border-dark/15 dark:border-cream/15',
      hoverColorClass: 'bg-cream-dark dark:bg-white/10',
    },
    'ol-white': {
      colorClass:
        'text-white bg-transparent focus:ring-primary-800 border border-white hover:bg-white hover:text-primary-500',
      hoverColorClass: '',
    },
    default: {
      colorClass:
        'text-dark bg-cream-dark dark:text-ivory dark:bg-white/20 focus:bg-cream-darker dark:focus:bg-white/40',
      hoverColorClass: 'bg-cream-darker dark:bg-white/10',
    },
  };

  return buttonClasses[btn || ''] || buttonClasses.default;
};

const getSizeClass = (size: string): string => {
  const sizeClasses: Record<string, string> = {
    xs: 'px-2.5 py-1.5 text-xs lg:px-2 lg:py-1',
    sm: 'py-2.5 px-4 lg:text-xs',
    md: 'px-6 py-3',
    lg: 'py-4 px-8',
  };
  return sizeClasses[size] || '';
};

export const Button: FC<PropsWithChildren<ButtonProps>> = ({
  className = '',
  loading = false,
  arrow = false,
  arrowLeft = false,
  btn = '',
  roundedFull = true,
  size = 'md',
  href = '',
  target = '_self',
  onClick,
  disabled = false,
  type = 'button',
  children,
}) => {
  const [pressed, setPressed] = useState(false);
  const { colorClass, hoverColorClass } = getColorClass(btn);
  const sizeClass = getSizeClass(size);
  const roundedClass = roundedFull ? 'rounded-full' : 'rounded-lg';
  const pressedClass =
    pressed && !disabled ? 'transform-scale scale-[.95]' : '';
  const disabledClass = disabled
    ? 'opacity-40 cursor-not-allowed'
    : 'cursor-pointer';

  const btnClass =
    `shrink-0 relative inline-flex items-center overflow-hidden text-sm font-bold tracking-wider uppercase transition-all duration-200 ease-in-out box-border group ${colorClass} ${sizeClass} ${roundedClass} ${disabledClass} ${pressedClass} ${className}`.trim();

  const buttonContent = (
    <>
      <div
        className={`absolute right-0 left-0 z-0 aspect-square w-full origin-center scale-0 rounded-full transition-all group-hover:scale-125 ${hoverColorClass}`}
        aria-hidden
      />
      {loading && <IconSpinner className="mr-3 -ml-1 h-5 w-5 animate-spin" />}
      {arrowLeft && <Arrow className="-my-1.5 rotate-180 px-2 py-1.5" />}
      <div className="relative z-0">{children}</div>
      {arrow && <Arrow className="-my-1.5 px-2 py-1.5" />}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        className={btnClass}
        onPointerDown={() => setPressed(true)}
        onPointerUp={() => setPressed(false)}
        onPointerCancel={() => setPressed(false)}
        aria-disabled={disabled}
        role="button">
        {buttonContent}
      </Link>
    );
  }

  return (
    <button
      className={btnClass}
      onClick={onClick}
      type={type}
      disabled={disabled}>
      {buttonContent}
    </button>
  );
};

const IconSpinner: React.FC<IconProps> = ({ className, title = 'Loading' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    aria-label="Loading">
    <title>{title}</title>
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const Arrow: React.FC<{ className?: string }> = ({ className = '' }) => (
  <figure
    className={`relative box-border flex items-center justify-center overflow-hidden ${className}`}>
    <svg
      className="h-3 translate-x-0 transition-all group-hover:-translate-x-8"
      viewBox="0 0 18 15"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M 1 7.4 L 15.857 7.4 M 17 7.4 L 10.6 1 M 17 7.4 L 10.6 13.8"
        strokeWidth="2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"></path>
    </svg>
    <svg
      className="absolute top-auto bottom-auto h-3 translate-x-8 transition-all group-hover:translate-x-0"
      viewBox="0 0 18 15"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M 1 7.4 L 15.857 7.4 M 17 7.4 L 10.6 1 M 17 7.4 L 10.6 13.8"
        strokeWidth="2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"></path>
    </svg>
  </figure>
);
